import { useMemo, useState } from 'react';
import { generateCpp, type GenResult } from '@spblock/core';
import { requireWorkspace } from '../lib/workspace';
import { useStore } from '../state/store';
import { IconClose, IconCopy, IconCheck, IconAlert, IconInfo } from './icons';

export function CodeModal() {
  const open = useStore((s) => s.codeModalOpen);
  const setOpen = useStore((s) => s.setCodeModalOpen);
  const [copied, setCopied] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const result: GenResult | null = useMemo(() => {
    if (!open) return null;
    try {
      return generateCpp(requireWorkspace());
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshKey]);

  if (!open) return null;

  const errors = result?.diagnostics.filter((d) => d.level === 'error') ?? [];
  const warnings = result?.diagnostics.filter((d) => d.level === 'warning') ?? [];

  async function onCopy(): Promise<void> {
    if (!result) return;
    await navigator.clipboard.writeText(result.cpp);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Generated C++ code">
      <div className="modal is-wide">
        <div className="modal-head">
          <div className="modal-title">Generated Arduino C++</div>
          <button className="btn btn-icon" onClick={() => setRefreshKey((k) => k + 1)} title="Regenerate from the current canvas">
            <IconInfo />
          </button>
          <button className="btn" onClick={() => void onCopy()} title="Copy the sketch">
            {copied ? <IconCheck /> : <IconCopy />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button className="modal-x" aria-label="Close" onClick={() => setOpen(false)}>
            <IconClose />
          </button>
        </div>

        <div className="modal-body" style={{ padding: 0 }}>
          {(errors.length > 0 || warnings.length > 0) && (
            <div style={{ padding: '12px 16px 0' }}>
              <ul className="diag-list">
                {errors.map((d, i) => (
                  <li key={`e${i}`} className="err">
                    <IconAlert width={14} height={14} />
                    {d.message}
                  </li>
                ))}
                {warnings.map((d, i) => (
                  <li key={`w${i}`} className="warn">
                    <IconAlert width={14} height={14} />
                    {d.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result && result.libraries.length > 0 && (
            <div style={{ padding: '12px 16px 0', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.8 }}>Required Arduino libraries:</span>
              {result.libraries.map((lib) => (
                <span
                  key={lib}
                  style={{
                    fontFamily: 'var(--font-mono, monospace)',
                    fontSize: 12,
                    padding: '2px 8px',
                    borderRadius: 999,
                    background: 'var(--surface-2, #eaf3fa)',
                    border: '1px solid var(--border, #d4e3ef)',
                  }}
                >
                  {lib}
                </span>
              ))}
            </div>
          )}
          <pre className="code-view">{result?.cpp ?? '// The canvas is empty.'}</pre>
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={() => setOpen(false)}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
